import type { ReactNode } from "react";

export type Series = { key: string; label: string };

/**
 * Title, subtitle, plot area, legend, and the table fallback.
 *
 * The legend is not optional for two or more series: identity must never be
 * carried by colour alone. The table view is the relief for the light-mode
 * slots that sit under 3:1, and it is what a screen reader gets.
 */
export function ChartFrame({
  title, subtitle, series, colors, height = 260, children, tableView,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  series: Series[];
  colors: string[];
  height?: number;
  children: ReactNode;
  /** Rows for the accessible table fallback: [rowLabel, ...values per series]. */
  tableView?: { header: string; rows: (string | number)[][] };
}) {
  return (
    <figure className="viz" style={{ margin: 0 }}>
      {title && <figcaption className="viz-title">{title}</figcaption>}
      {subtitle && <p className="viz-sub">{subtitle}</p>}

      <div style={{ width: "100%", height }}>{children}</div>

      {series.length > 1 && (
        <div className="viz-legend">
          {series.map((s, i) => (
            <span className="viz-legend-item" key={s.key}>
              <span className="viz-swatch" style={{ background: colors[i] }} />
              {s.label}
            </span>
          ))}
        </div>
      )}

      {tableView && (
        <details style={{ marginTop: "var(--s-5)" }}>
          <summary className="viz-legend-item" style={{ cursor: "pointer" }}>
            View as table
          </summary>
          <div className="table-wrap" style={{ marginTop: "var(--s-4)" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>{tableView.header}</th>
                  {series.map((s) => <th key={s.key} className="cell-right">{s.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {tableView.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((cell, j) => (
                      <td key={j} className={j === 0 ? undefined : "cell-num"}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </figure>
  );
}

/** Shared Recharts tooltip so every chart's hover looks identical. */
export function VizTooltip({ active, payload, label, formatter }: {
  active?: boolean;
  payload?: { name?: string; value?: number | string; color?: string; dataKey?: string }[];
  label?: string | number;
  formatter?: (v: number | string) => string;
}) {
  if (!active || !payload?.length) return null;
  const fmt = formatter ?? ((v: number | string) => String(v));
  return (
    <div className="viz-tip">
      {label !== undefined && (
        <div className="viz-tip-key" style={{ marginBottom: 4 }}>{label}</div>
      )}
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="viz-swatch" style={{ background: p.color }} />
          {/* Name in text tokens, never the series colour. */}
          <span className="viz-tip-key" style={{ flex: 1 }}>{p.name}</span>
          <span className="viz-tip-val">{fmt(p.value ?? "")}</span>
        </div>
      ))}
    </div>
  );
}
